import { Box } from '@factorialco/deprecated-design-system'
import { OnePagination, useDataCollectionData } from '@factorialco/f0-react/dist/experimental'

import type Employee from 'modules/employees/store/models/Employee'
import type SupplementsFormStore from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/store'
import type { ContractTaxonomyFragment } from 'generated/resources/graphql'
import type { useSupplementsEmployeesDataCollectionSource } from 'modules/payroll_bundle/components/Supplements/useSupplementsEmployeesDataCollectionSource'

import { SupplementTableRowCell } from './SupplementTableRowCell'

type SourceType = ReturnType<typeof useSupplementsEmployeesDataCollectionSource>

export interface SupplementsCustomVisualizationByDateProps {
  source: SourceType
  employeesById: Record<number, Employee>
  supplementsStore: SupplementsFormStore
  loadingSupplements: boolean
  taxonomies: ContractTaxonomyFragment[]
  itemToHighlight?: { employeeId?: number; supplementId?: number } | null
  employeeExpansionState: Record<string, boolean>
  toggleEmployeeExpansion: (id: string) => void
  toggleTaxonomyCreationModal: (value: boolean) => void
}

/**
 * Custom DataCollection visualization for the date/frequency view.
 *
 * This is similar to SupplementsCustomVisualization but works with the
 * EmployeesCollectionList data source which has records with `id` (employee ID)
 * instead of `employeeId`.
 *
 * This view does not display salary information since there's no policy period
 * context to calculate estimated salaries from.
 */
export function SupplementsCustomVisualizationByDate({
  source,
  employeesById,
  supplementsStore,
  loadingSupplements,
  taxonomies,
  itemToHighlight,
  employeeExpansionState,
  toggleEmployeeExpansion,
  toggleTaxonomyCreationModal,
}: SupplementsCustomVisualizationByDateProps) {
  const { data: collectionData, paginationInfo, setPage, isLoading } = useDataCollectionData(source)
  const records = collectionData?.records ?? []

  const pageBasedInfo =
    paginationInfo &&
    'type' in paginationInfo &&
    paginationInfo.type === 'pages' &&
    'currentPage' in paginationInfo &&
    'pagesCount' in paginationInfo
      ? paginationInfo
      : null

  return (
    <Box width="full" gap="s8">
      {records.map((record, index) => {
        const employee = employeesById[record.id]
        const isLast = index === records.length - 1
        // No salary data in date/frequency view since there's no policy period
        return (
          <SupplementTableRowCell
            key={record.id}
            employee={employee}
            isLast={isLast}
            supplementsStore={supplementsStore}
            loading={loadingSupplements}
            taxonomies={taxonomies}
            estimatedSalary={undefined}
            itemToHighlight={itemToHighlight}
            shouldScrollIntoView={null}
            employeeExpansionState={employeeExpansionState}
            toggleEmployeeExpansion={toggleEmployeeExpansion}
            toggleTaxonomyCreationModal={toggleTaxonomyCreationModal}
          />
        )
      })}
      {pageBasedInfo !== null && (
        <OnePagination
          totalPages={pageBasedInfo.pagesCount}
          currentPage={pageBasedInfo.currentPage}
          onPageChange={setPage}
          disabled={isLoading}
        />
      )}
    </Box>
  )
}
